import {useState, useCallback} from 'react';

import {useStores} from '../../../Hooks';
import {accessItemTypeEnum} from '../../../serverClientShared/sharedEnums';
import useAccessState from '../SettingsShared/useAccessState';

const useFolderSettings = () => {
    const {modalStore, projectStore, generalStore} = useStores();
    const access = useAccessState();

    const [folder, setFolder] = useState(null);
    const [originalName, setOriginalName] = useState('');
    const [editedName, setEditedName] = useState('');

    const initFromFolder = useCallback((f) => {
        if (!f) return;
        setFolder(f);
        setOriginalName(f.FolderName || '');
        setEditedName(f.FolderName || '');
    }, []);

    const isFieldsDirty = editedName !== originalName;
    const isDirty = isFieldsDirty || access.isAccessDirty;

    const executeSave = useCallback(async () => {
        generalStore.isLoaderOpen = true;
        try {
            const itemRef = {itemId: folder.FolderIdentifier, itemType: accessItemTypeEnum.FOLDER, nestedRoute: folder.NestedFolderRoute || ''};
            const tasks = [access.saveAccessChanges(generalStore.workspace, itemRef).catch((e) => console.error('Access save failed:', e))];
            if (editedName !== originalName) tasks.push(projectStore.renameFolder(folder.FolderIdentifier, editedName));
            await Promise.all(tasks);
            await projectStore.refreshProjectDetails(true);
            setOriginalName(editedName);
        } catch (e) {
            console.error('Failed to save folder settings:', e);
            modalStore.showErrorMessage(e.message || generalStore.translate('somethingWentWrong'));
        }
        generalStore.isLoaderOpen = false;
    }, [folder, editedName, originalName, generalStore, projectStore, modalStore, access]);

    const save = useCallback(async () => {
        if (!folder || !isDirty) return;
        await executeSave();
    }, [folder, isDirty, executeSave]);

    const discard = useCallback(() => {
        setEditedName(originalName);
        access.discardAccess();
    }, [originalName, access]);

    return {
        folder, initFromFolder,
        itemId: folder?.FolderIdentifier,
        nestedRoute: folder?.NestedFolderRoute,
        itemType: accessItemTypeEnum.FOLDER,
        editedName, setEditedName, originalName,
        isDirty, save, discard,
        ...access,
    };
};

export default useFolderSettings;
